import { useEffect, useRef } from 'react'
import {
  moveLeft, moveRight, moveUp, moveDown,
  pause, moveToTarget
} from '../lib/behaviours'
import { getRandomFromArray } from 'utils'
import { useCollision } from '../hooks/use-collision'
import { usePosition } from '../hooks/use-position'
import { useWorld } from '../hooks/use-world'
import { usePathfinding } from '../hooks/use-pathfinding'
import { useWander } from '../hooks/use-wander'
import { usePursuit } from '../hooks/use-pursuit'
import { distanceTo } from '../lib/geometry'
import { tileBoundaryToPixels, isOutOfBounds } from '../lib/boundary'
import {
  GRID_SIZE,
  MONSTER_CHASE_RANGE, MONSTER_BACK_OFF_RANGE, MONSTER_DAMAGE_COOLDOWN,
  MONSTER_CHASE_RECOMPUTE_INTERVAL, MONSTER_STUCK_THRESHOLD,
  XP_MONSTER_MIN, XP_MONSTER_HP_DIVISOR,
} from '../config/constants'

const patrolBehaviours = [
  moveLeft, moveRight, moveUp, moveDown,
  pause, pause, pause, pause, pause
]

export const Monster = ({
  x = 40,
  y = 40,
  width = GRID_SIZE,
  height = GRID_SIZE,
  config = {},
  ...rest
}) => {
  const cfg = {
    stride: 1,
    solid: true,
    damage: 5,
    health: 30,
    ...config,
    entityType: 'monster'
  }

  const maxHealth = cfg.health
  const healthRef = useRef(maxHealth)
  const deadRef = useRef(false)

  const boundary = tileBoundaryToPixels(cfg.boundary, width, height)
  const pixelBoundaryRef = useRef(boundary)

  // Track repeated collisions without triggering re-renders
  const collisionsRef = useRef({ count: 0, sprite: null })
  const damageCooldownRef = useRef(0)

  const [position, update] = usePosition(x, y, width, height, cfg)
  const posRef = useRef(position)
  useEffect(() => { posRef.current = position }, [position])

  const { frame, spritesRef, takeDamage, pendingDamageRef, removeSprite, killMonster, gainXp, addDroppedItem, pendingLadderChoiceRef, currentLevel } = useWorld()
  cfg.level = currentLevel
  const { isColliding } = useCollision()
  const { findPath } = usePathfinding()
  const { behaviourRef } = useWander({ behaviors: patrolBehaviours, cfg, pixelBoundaryRef, isColliding, update })
  const { getWaypoint: getChaseWaypoint, reportMoveResult: reportChase, resetPursuit: resetChase } =
    usePursuit({ findPath, width, height, stride: cfg.stride })

  useEffect(() => {
    if (deadRef.current || pendingLadderChoiceRef.current) return

    // ── Incoming damage ───────────────────────────────────────────────────────
    const incoming = pendingDamageRef.current[position.id]
    if (incoming) {
      delete pendingDamageRef.current[position.id]
      const newHealth = Math.max(0, healthRef.current - incoming)
      healthRef.current = newHealth
      // Write health into the sprite entry so Canvas can draw a health bar
      if (spritesRef.current[position.id]) {
        spritesRef.current[position.id].health = { current: newHealth, max: maxHealth }
      }
      if (newHealth <= 0) {
        deadRef.current = true
        removeSprite({ id: position.id })
        gainXp(cfg.xp ?? Math.max(XP_MONSTER_MIN, Math.ceil(maxHealth / XP_MONSTER_HP_DIVISOR)))
        killMonster?.(position.id)
        const drops = cfg.drops ?? []
        drops.forEach((drop, i) => {
          if (Math.random() * 100 > (drop.chance ?? 100)) return
          addDroppedItem({
            id: `drop_${Date.now()}_${position.id}_${i}`,
            type: drop.type || 'treasure',
            name: drop.name || drop.type || 'treasure',
            value: drop.value ?? 10,
            illumination: drop.illumination ?? 0,
            lifespan: drop.lifespan ?? 0,
            x: position.x,
            y: position.y,
            dropped: true,
            level: currentLevel,
          })
        })
        return
      }
    }

    if (damageCooldownRef.current > 0) damageCooldownRef.current--

    const pos = posRef.current
    const player = spritesRef.current?.player?.position

    let nextPos
    if (player) {
      const mCx = pos.x + width / 2
      const mCy = pos.y + height / 2
      const pCx = player.x + (player.width ?? GRID_SIZE) / 2
      const pCy = player.y + (player.height ?? GRID_SIZE) / 2
      const dist = distanceTo({ x: mCx, y: mCy }, { x: pCx, y: pCy })
      if (dist < MONSTER_BACK_OFF_RANGE) {
        // Too close — deal damage and back off directly (no pathfinding needed)
        if (damageCooldownRef.current === 0) {
          takeDamage(cfg.damage)
          damageCooldownRef.current = MONSTER_DAMAGE_COOLDOWN
        }
        nextPos = moveToTarget(
          pos.x + (pos.x - player.x),
          pos.y + (pos.y - player.y),
          pos,
          cfg.stride
        )
        resetChase()
      } else if (dist < MONSTER_CHASE_RANGE) {
        // Chase via A* path — recompute periodically or when stuck
        const { waypoint } = getChaseWaypoint(pos, player, {
          recomputeInterval: MONSTER_CHASE_RECOMPUTE_INTERVAL,
          stuckThreshold: MONSTER_STUCK_THRESHOLD,
        })
        nextPos = moveToTarget(waypoint?.x ?? player.x, waypoint?.y ?? player.y, pos, cfg.stride)
      } else {
        nextPos = behaviourRef.current(pos, cfg.stride)
      }
    } else {
      resetChase()
      nextPos = behaviourRef.current(pos, cfg.stride)
    }

    const colliding = isOutOfBounds(nextPos, boundary) ? true : isColliding(nextPos)
    if (!colliding) {
      update(nextPos.x, nextPos.y)
      collisionsRef.current = { count: 0, sprite: null }
      reportChase(true)
    } else {
      reportChase(false)
      if (colliding === true || colliding.position?.id !== 'player') {
        const prev = collisionsRef.current
        collisionsRef.current = { sprite: colliding, count: prev.count + 1 }
        behaviourRef.current = getRandomFromArray(patrolBehaviours)

        // Unstick if repeatedly blocked by the same non-player sprite
        if (colliding !== true && collisionsRef.current.count > 3) {
          const offsets = [
            { x: width, y: 0 }, { x: -width, y: 0 },
            { x: 0, y: height }, { x: 0, y: -height }
          ]
          const stuck = colliding.position
          for (const off of offsets) {
            const candidate = { ...pos, x: stuck.x + off.x, y: stuck.y + off.y }
            if (!isColliding(candidate)) {
              update(candidate.x, candidate.y)
              collisionsRef.current = { count: 0, sprite: null }
              break
            }
          }
        }
      }
    }
  }, [frame])

  // Canvas renders monsters visually — no DOM output needed here
  return null
}
